import { useParams } from "wouter";

/**
 * Module-level one-time consumption of the admin_thf handoff cookie.
 *
 * The server-side admin URL token redirect (INC-2026-07-08) intercepts GET
 * requests to /admin/PAGE/HEX64TOKEN, sets the token in a short-lived (60 s)
 * SameSite=Strict cookie named admin_thf, and redirects to the tokenless URL.
 * Reading the cookie here — at module load, before any component renders —
 * ensures the token is captured in the first render cycle, persisted to
 * localStorage for subsequent renders, and the cookie is cleared immediately
 * so it cannot be replayed after an accidental page reload within the TTL.
 */
let _handoffToken = "";
if (typeof document !== "undefined") {
  const m = document.cookie.match(/(?:^|;\s*)admin_thf=([0-9a-f]{64})/i);
  if (m?.[1]) {
    _handoffToken = m[1];
    // Consume immediately — prevent replay within the 60 s cookie TTL
    document.cookie =
      "admin_thf=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict";
    try {
      localStorage.setItem("ngu_admin_token", _handoffToken);
    } catch {
      // Private browsing — token remains in _handoffToken for this render cycle
    }
  }
}

/**
 * Returns both the raw URL token and the effective admin key token for the current page.
 *
 * - `urlToken`: the raw value from the URL path param (`:token` or `:token?`).
 *   Empty string when the URL has no token (e.g. after `window.history.replaceState`
 *   strips it, or when navigating back to a tokenless URL on refresh).
 *   Use this ONLY for the replaceState strip effect condition so the strip never
 *   fires on tokenless refreshes and never clobbers `window.location.search`.
 *
 * - `token`: the effective admin key resolved in priority order:
 *   1. URL path token (`:token` param) — present on direct navigation before replaceState runs
 *   2. admin_thf handoff cookie — populated by the server-side redirect when the original
 *      URL contained a token (INC-2026-07-08); already consumed at module load above
 *   3. ngu_admin_token in localStorage — the persisted token from a previous successful auth
 *
 * All admin pages that strip their URL token should use this hook instead of
 * destructuring `useParams()` directly.
 */
export function useAdminPageToken(): { token: string; urlToken: string } {
  const params = useParams<Record<string, string>>();
  const urlToken: string = (params as Record<string, string>)?.token ?? "";
  const effectiveToken =
    urlToken ||
    _handoffToken ||
    (typeof window !== "undefined"
      ? (localStorage.getItem("ngu_admin_token") ?? "")
      : "");
  return { token: effectiveToken, urlToken };
}
